import {Component, OnDestroy, OnInit, ChangeDetectionStrategy} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";
import {Store} from "@ngrx/store";
import {Observable} from "rxjs";
import {Exception, RemarkData} from "../../models/remarkDataModel";
import {
    selectIsLoadingTrafficData,
    selectRemarkData,
    selectTrafficData
} from "../../store/createoredit/createoredit.selector";
import {
    clearRemarkData,
    fetchCurrentAndNextYearTrafficData,
    fetchRemarkData,
} from "../../store/createoredit/createoredit.action";
import {Data, GetAllWrappedResponse} from "../../models/getAllWrappedDataModel";
import {FormBuilder, FormControl, Validators} from "@angular/forms";
import {Select2Data, Select2Value} from "ng-select2-component";
import {AuthService} from "../../service/auth.service";
import {userReadWriteGroups} from "../../../assets/user-groups";
import {CalendarService} from "../../service/calendar.service";
import {
    createExceptionData,
    updateExceptionData
} from "../../store/createorupdateexception/createorupdateexception.action";
import {CreateExceptionFormGroup} from "../../models/createExceptionFormGroupModel";
import {CreateOrUpdateExceptionPayloadModel} from "../../models/createOrUpdateExceptionPayloadModel";
import {environment} from "../../../environments/environment";

type Month = { header: string, formControl: FormControl<Date[] | null | undefined>, start: Date, end: Date };
type Selected = {
    disabledDates: Date[];
    calendarMonths: Array<Month>;
    name: string;
    validTrains: GetAllWrappedResponse["data"][number]["validTrains"];
    selectableTrainNumberOptions: Select2Data;
    selectedTrainIds: Select2Value[];
    trafficData: Data | null;
    startDate: Date;
    endDate: Date;
};

@Component({
    selector: "app-create-or-update-exception",
    templateUrl: "./create-or-update-exception.component.html",
    styleUrl: "./create-or-update-exception.component.scss",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CreateOrUpdateExceptionComponent implements OnInit, OnDestroy {
    trafficData$ = new Observable<GetAllWrappedResponse["data"]>();
    trafficData!: GetAllWrappedResponse["data"];
    remarkData$ = new Observable<RemarkData | null>();
    remarkData: RemarkData | null = null; // Remark is not multi-year supported, but just connected to id
    remarkId: string = '';
    exceptionId: string = '';
    isEditMode: boolean = false;
    isLoading$: Observable<boolean>;
    form: CreateExceptionFormGroup = this.generateFormGroup({
        id: 0,
        name: '',
        dates: [],
        weekdaySelected: false,
        saturdaySelected: false,
        sundaySelected: false,
    });
    selectedYear: Selected = {
        disabledDates: [],
        calendarMonths: [],
        name: "",
        validTrains: [],
        selectableTrainNumberOptions: [],
        selectedTrainIds: [],
        trafficData: null,
        startDate: new Date(),
        endDate: new Date(),
    }
    exceptionData: RemarkData["exceptions"][number] | null = null;

  constructor(
        private authService: AuthService,
        private calendarService: CalendarService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private fb: FormBuilder,
        private store: Store) {
        this.trafficData$ = this.store.select(selectTrafficData);
        this.remarkData$ = this.store.select(selectRemarkData);
        this.isLoading$ = this.store.select(selectIsLoadingTrafficData);
    }

    ngOnInit(): void {
        this.authService.hasReadWriteGroups(userReadWriteGroups.DLI);
        this.store.dispatch(fetchCurrentAndNextYearTrafficData());

        this.activatedRoute.paramMap.subscribe(param => {
            this.remarkId = param.get("remarkId")!;
            this.exceptionId = param.get("exceptionId")!;
            this.isEditMode = !!this.exceptionId;
            if (this.remarkId) {
                this.store.dispatch(fetchRemarkData({ remarkId: this.remarkId }))
            }
        });

        this.trafficData$.subscribe(trafficData => {
            this.trafficData = trafficData;

            this.remarkData$.subscribe(remark => {
                this.remarkData = remark;

                if (this.isEditMode) {
                    this.exceptionData = remark?.exceptions.find((e) => e.id == Number(this.exceptionId))!;
                    this.form = this.generateFormGroup({ ...this.exceptionData });
                } else {
                    // is create mode
                    this.form = this.generateFormGroup({
                        id: null,
                        name: '',
                        dates: [],
                        weekdaySelected: false,
                        saturdaySelected: false,
                        sundaySelected: false,
                    });
                }
                if (trafficData[0]) {
                    this.setSelectedYearData(trafficData[0]);
                }
            })
        })
    }

    ngOnDestroy(): void {
        // Needed to not flash previous page's selectedTrains
        this.store.dispatch(clearRemarkData());
    }

    generateFormGroup(remark: Pick<Exception, "id" | "name" | "dates" | "weekdaySelected" | "saturdaySelected"| "sundaySelected">): CreateExceptionFormGroup {
        return this.fb.nonNullable.group({
            id: this.fb.nonNullable.control(remark.id),
            name: this.fb.nonNullable.control(remark.name, [
                Validators.required,
                Validators.maxLength(50)
            ]),
            dates: this.fb.nonNullable.control(remark.dates || []),
            weekdaySelected: this.fb.nonNullable.control(remark.weekdaySelected),
            saturdaySelected: this.fb.nonNullable.control(remark.saturdaySelected),
            sundaySelected: this.fb.nonNullable.control(remark.sundaySelected),
        }) as CreateExceptionFormGroup;
    }

    setSelectedYearData(trafficData: GetAllWrappedResponse["data"][number]) {
        // First clear all changed year-dependent inputs
        this.form.controls.weekdaySelected.reset()
        this.form.controls.saturdaySelected.reset()
        this.form.controls.sundaySelected.reset()

        // Then set all initial values for selected year
        this.selectedYear.trafficData = trafficData;
        this.selectedYear.name = trafficData.name;
        this.selectedYear.validTrains = trafficData.validTrains;
        this.selectedYear.startDate = new Date(trafficData.startDate)
        this.selectedYear.endDate = new Date(trafficData.endDate)

        if (this.exceptionData?.trainNumbers) {
            // remark is not multi-year compliant, but we want to reset the selected trains when switching years.
            this.selectedYear.selectedTrainIds = this.exceptionData.trainNumbers || [];
        }

        const yearDates = this.calendarService.getMonthsBetweenDates(trafficData.startDate, trafficData.endDate);
        const preselectedDates = this.exceptionData?.dates.map(date => new Date(date)) || [];
        this.selectedYear.calendarMonths = yearDates.map(monthDate => {
            const selectedDatesInMonth = preselectedDates.filter(a => a.getMonth() === monthDate.getMonth() && a.getFullYear() === monthDate.getFullYear())
            return ({
                header: monthDate.toLocaleString('sv-SE', {month: 'long', year: 'numeric'}),
                formControl: new FormControl(selectedDatesInMonth.length ? selectedDatesInMonth : null),
                start: monthDate,
                end: this.calendarService.getLastDayOfMonth(monthDate),
            });
        });

        this.selectedYear.disabledDates = this.getDisabledDates();
    }

    getDisabledDates() {
        const allDates = this.getDatesBetween(this.selectedYear.startDate, this.selectedYear.endDate);
        const selectableDates = this.remarkData?.dates.map(date => new Date(date));

        if (selectableDates?.length && allDates.length) {
            return this.excludeDates(allDates, selectableDates);
        } else {
            return [];
        }
    }

    excludeDates(dates1: Date[], dates2: Date[]): Date[] {
        // Convert all dates in the second array to 'yyyy-MM-dd' strings for comparison
        const formattedDates2 = new Set(dates2.map(this.calendarService.convertToStringDateWithTimezone));

        // Filter dates1 to only include dates not in formattedDates2
        return dates1.filter(date => !formattedDates2.has(this.calendarService.convertToStringDateWithTimezone(date)));
    }


    getDatesBetween(startDate: Date, endDate: Date): Date[] {
        const dates: Date[] = [];

        // Ensure that startDate is less than or equal to endDate
        if (startDate > endDate) {
            throw new Error("The start date cannot be after the end date.");
        }

        // Copy startDate to a new Date object to avoid modifying the original
        let currentDate = new Date(startDate);

        // Loop through all dates between startDate and endDate
        while (currentDate <= endDate) {
            dates.push(new Date(currentDate));  // Add the current date to the array
            currentDate.setDate(currentDate.getDate() + 1);  // Move to the next day
        }

        return dates;
    }

    goToRemark() {
        this.router.navigate(['/admin/edit/', this.remarkId]);
    }

    private generatePayload(): CreateOrUpdateExceptionPayloadModel {
        const selectedDates = this.selectedYear.calendarMonths.flatMap(date => date.formControl.value!).filter(Boolean) || [];
        const id = this.form.value.id ? Number.parseInt(this.exceptionId) : 0;

        return {
            id: id,
            name: this.form.value.name!,
            dates: selectedDates.map(date => this.calendarService.convertToStringDateWithTimezone(date)),
            trainNumbers: this.selectedYear.selectedTrainIds as string[],
            weekdaySelected: Boolean(this.form.value.weekdaySelected),
            saturdaySelected: Boolean(this.form.value.saturdaySelected),
            sundaySelected: Boolean(this.form.value.sundaySelected),
            trainOwner: environment.trainOwnerSetting,
        };
    }

    updateException() {
        if (!this.form.valid) return;
        const payload = this.generatePayload();
        this.store.dispatch(updateExceptionData({ remarkId: this.remarkId, exception: payload }))
    }

    createException() {
        if (!this.form.valid) return;
        const payload = this.generatePayload();
        this.store.dispatch(createExceptionData({ remarkId: this.remarkId, exception: payload }))
    }

    toggleTrafficYear(selectedTrafficYear: string) {
        const selectedTrafficYearData = this.trafficData.find(trafficYear => trafficYear.name === selectedTrafficYear);
        if (selectedTrafficYearData && selectedTrafficYearData.name !== this.selectedYear.name) {
            this.setSelectedYearData(selectedTrafficYearData)
        }
    }

    hasSelectedTrain(trainId: string) {
        return this.selectedYear.selectedTrainIds.findIndex(a => String(a).includes(trainId)) >= 0;
    }

    toggleSelectedTrain(trainId: string) {
        const selectedTrains = new Set(this.selectedYear.selectedTrainIds);
        if (this.hasSelectedTrain(trainId)) {
            selectedTrains.delete(trainId);
        } else {
            selectedTrains.add(trainId);
        }

        this.selectedYear.selectedTrainIds = Array.from(selectedTrains);
    }

}
